import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth.js";
import { inboxApi } from "../inbox/api.js";
import {
  retentionFollowUpApi,
  type RetentionFollowUpCategory,
  type RetentionFollowUpStatus,
  type RetentionFamilyFollowUpEmailStatus,
} from "../booking/retentionFollowUps.js";
import { DateField } from "../calendar/DateField.js";
import { Pagination } from "../Pagination.js";
import { DENSE_ROW_PAGE_SIZE } from "../paginationPageSizes.js";
import {
  isEverybody,
  isTicked,
  toggleAll,
  toggleTutor,
  type TutorOption,
  type Whose,
} from "../calendar/entries.js";
import { formatDate, isoDay } from "../format.js";
import { useAsync } from "../useAsync.js";
import { TableScrollRegion } from "../ui/TableScrollRegion.js";
import type {
  PaymentProviderPage,
  PaymentReport,
  PaymentReportQuery,
  PersonReport,
  ReportQuery,
  ReportingDataSource,
  RetentionBookingPatternReview,
  RetentionCancellationFollowUpReview,
  RetentionUnusedCreditFollowUpReview,
  RetentionRisk,
  RetentionTasterFollowUpReview,
} from "../booking/staff.js";
import { paymentMethodLabel } from "../booking/methods.js";
import { bookingSystemDisplayText } from "../booking/staffDisplayCopy.js";
import {
  chosenProviders,
  describeChosen,
  namesByProvider,
  peopleOptions,
  reportPeople,
} from "../booking/people.js";
import {
  byProvider,
  mergeAttention,
  NOT_NARROWED,
  staffBooking,
} from "../booking/staff.js";
import { NEEDS_ORGANISATION_SCOPE, scopeChoice } from "../booking/scope.js";
import { AttendanceAnalytics } from "./reports/AttendanceAnalytics.js";
import { studentBookLessonHref, studentDetailHref } from "./Students.js";

/**
 * The capabilities this workspace actually has a panel for.
 *
 * `reports.*` is wider than this screen: `reports.operations.read` belongs to
 * the Operations page and `reports.organisation.read` only widens scope.
 * Gating the sidebar link or the "any report" check on the `reports.` prefix
 * would hand an Operations-only reporter a workspace of filters with nothing
 * underneath them, so both use this list. Keep it in step with the panels
 * rendered near the end of `Reports`.
 */
export const REPORT_WORKSPACE_CAPABILITIES = [
  "reports.students.read",
  "reports.attendance.read",
  "reports.payments.read",
  "reports.credits.read",
  "reports.earnings.read",
  "reports.utilisation.read",
] as const;

/** Whether the account holds a capability the Reports workspace renders a panel for. */
export function hasWorkspaceReport(capabilities: Iterable<string>): boolean {
  const held =
    capabilities instanceof Set ? capabilities : new Set(capabilities);
  return REPORT_WORKSPACE_CAPABILITIES.some((capability) =>
    held.has(capability),
  );
}

/**
 * The business reports.
 *
 * Seven endpoints, one screen, arranged by the question somebody actually came
 * with rather than by which service computes them. "Is anybody drifting away",
 * "are lessons being taught", "what has been taken and what is still owed",
 * "how much of the timetable is used" — the split between Hub data and FastAPI
 * data is invisible here, and should be.
 *
 * **Scope is a real choice, not a filter.** A tutor sees their own figures and
 * that is the whole report as far as they are concerned; only somebody with
 * organisation access can widen it, and the control is hidden from everybody
 * else rather than shown and refused. Which reports appear at all follows the
 * `reports.*` capabilities the account holds — FastAPI checks each again, so
 * this only decides what is worth offering.
 *
 * **Who is a filter, and it is the diary's.** Somebody with the whole school in
 * front of them usually wants three of it: two tutors and the one who covered
 * for them, this half-term against last. So the same row of tick boxes the
 * calendar has sits under the dates, built from the same functions, and ticking
 * names turns each report from one whole-school figure into a line per person
 * — which is the shape you compare in, and the shape a whole-school total
 * cannot be divided back into by eye.
 *
 * The two reports that are *already* per tutor — earnings and timetable — are
 * narrowed here, by dropping the rows nobody asked about. The other four are
 * aggregates and can only be narrowed where they are computed, so each person
 * is asked for separately and the answer is checked against who it was asked
 * about. See `byProvider`.
 *
 * **Coverage notes are shown, not buried.** Confirmed earnings exclude fees, tax
 * and disputes; the timetable percentage is measured against each tutor's
 * *current* Amelia working pattern, which is not what it was three months ago. A
 * figure printed without that is a figure that will be reconciled against a bank
 * statement and found wanting.
 */
export function Reports({
  paymentOnly = false,
  initialStart,
  initialEnd,
  initialScope,
}: {
  paymentOnly?: boolean;
  initialStart?: string;
  initialEnd?: string;
  initialScope?: "mine" | "organisation";
} = {}) {
  const { me } = useAuth();
  const held = new Set((me?.capabilities ?? []).filter((capability) => !paymentOnly || capability === "reports.payments.read"));
  const title = paymentOnly ? "Payment history" : "Reports";
  const choice = scopeChoice(me);

  const [start, setStart] = useState(() => initialStart ?? isoDay(-30));
  const [end, setEnd] = useState(() => initialEnd ?? isoDay(0));
  const [scope, setScope] = useState<"mine" | "organisation">(
    initialScope ?? choice.initial,
  );

  useEffect(() => {
    setStart(initialStart ?? isoDay(-30));
    setEnd(initialEnd ?? isoDay(0));
    setScope(initialScope ?? choice.initial);
  }, [initialStart, initialEnd, initialScope, choice.initial]);

  /**
   * Whose figures. `"all"` from the start, and not remembered between visits.
   *
   * The diary makes the opposite choice for the same reason: there, the safe
   * answer is your own lessons and returning to a stale set of colleagues would
   * be a surprise. Here the safe answer is everybody — a report that silently
   * left two tutors out because of a box ticked last Thursday is a report
   * somebody would quote — so that is what the page opens on every time.
   */
  const [whose, setWhose] = useState<Whose>("all");

  /**
   * Amelia provider ids, given names.
   *
   * One request for two jobs: the row of tick boxes, and the names in the
   * earnings and timetable tables. A provider with no matching tutor profile
   * keeps its number rather than becoming a blank row, and cannot be ticked —
   * which is honest, since there is nobody to tick.
   */
  const people = useAsync(() => reportPeople().catch(() => []), []);
  const names = namesByProvider(people.data ?? []);
  const options = peopleOptions(people.data ?? []);

  if (!me?.booking.staff) {
    return (
      <>
        <h1>{title}</h1>
        <p className="empty">
          This account does not have staff access to the booking system, so reports are unavailable.
        </p>
      </>
    );
  }

  const anyReport = hasWorkspaceReport(held);
  if (!anyReport) {
    return (
      <>
        <h1>{title}</h1>
        <p className="empty">
          You have not been given access to any reports. An owner can grant them
          on the staff page.
        </p>
      </>
    );
  }

  /* Granted reports that cannot be asked for. An account with no Amelia
     provider has no lessons of its own, so `mine` can never be answered, and
     without the whole-school permission there is no other scope left. Said
     here, once, rather than drawn as six panels each carrying FastAPI's own
     wording about a provider link the reader has never heard of. */
  if (!choice.possible) {
    return (
      <>
        <h1>{title}</h1>
        <p className="empty">{NEEDS_ORGANISATION_SCOPE}</p>
      </>
    );
  }

  /**
   * A window, once there is one.
   *
   * A date box can be emptied — the clear control in the browser's own picker,
   * or selecting the contents and deleting them — and an empty box is not a
   * date. `start_date` and `end_date` are required by every report operation,
   * and the request builder drops an empty parameter rather than sending a
   * blank one, so the reports were fetched without them and all seven refused
   * at once with a validation error nobody could act on. The header line
   * rendered "Invalid Date" beside it.
   *
   * So a half-filled window asks for nothing. The reports keep whatever they
   * last loaded, and the page says which box needs filling in.
   */
  const complete = start !== "" && end !== "";
  const query: ReportQuery = { start_date: start, end_date: end, scope };

  /**
   * Whether picking people is a question worth asking at all.
   *
   * Only against the whole school: "my own figures, broken down by which of me
   * taught them" is not a question, and a tutor looking at their own lessons
   * has one person to choose from. And only where there is more than one name,
   * for the reason the diary gives — a choice between one thing and the same
   * thing teaches people that the controls on this page do nothing.
   */
  const narrowable = scope === "organisation" && options.length > 1;

  /**
   * Null means everybody, which is the request the page has always made. An
   * empty array is every box cleared, which is a state of its own and is said
   * rather than drawn as six empty panels.
   */
  const chosen = narrowable ? chosenProviders(whose, options) : null;
  const nobodyTicked = chosen !== null && chosen.length === 0;

  const covering =
    chosen === null
      ? scope === "organisation"
        ? "The whole school"
        : "Your own lessons"
      : describeChosen(chosen, options);

  const panels = { query, chosen, names };

  return (
    <>
      <h1>{title}</h1>
      {paymentOnly && <p>Payments received, refunds and money owed for the selected period. This is the existing payment report, not a transaction-by-transaction ledger.</p>}
      <p className="lede">
        {covering}
        {complete && (
          <>
            {" · "}
            {formatDate(`${start}T00:00:00Z`)} to{" "}
            {formatDate(`${end}T00:00:00Z`)}
          </>
        )}
      </p>

      <div className="panel">
        <div className="fields">
          <DateField label="From" onChange={setStart} value={start} />
          <DateField label="To" onChange={setEnd} value={end} />
          {/* Only offered where there is a real choice — the whole-school
              permission *and* a provider of their own to narrow to. A control
              that always answers 403 teaches people to distrust the page, and
              one whose other option is refused upstream is no better. */}
          {choice.choosable && (
            <label>
              <span className="small muted">Covering</span>
              <select
                value={scope}
                onChange={(event) =>
                  setScope(event.target.value as "mine" | "organisation")
                }
              >
                <option value="organisation">The whole school</option>
                <option value="mine">My own lessons</option>
              </select>
            </label>
          )}
        </div>

        {/* The calendar's row of boxes, in the calendar's own classes, because
            it is the same control answering the same question and somebody who
            has used one has used the other. Left in place while it is hidden —
            switching to "my own lessons" and back should not lose the two names
            you had picked. */}
        {narrowable && (
          <div
            className="cal-whose"
            role="group"
            aria-label="Whose figures to report on"
          >
            <span className="small muted">Staff</span>
            <label className="cal-tick">
              <input
                checked={isEverybody(whose, options)}
                onChange={() => setWhose(toggleAll(whose, options))}
                type="checkbox"
              />
              Everyone
            </label>
            {options.map((option) => (
              <label className="cal-tick" key={option.value}>
                <input
                  checked={isTicked(whose, option.value)}
                  onChange={() =>
                    setWhose(toggleTutor(whose, options, option.value))
                  }
                  type="checkbox"
                />
                {option.label}
              </label>
            ))}
          </div>
        )}
      </div>

      {!complete && (
        <div className="notice">
          Choose {start === "" ? "a start date" : "an end date"} to see the
          reports.
        </div>
      )}

      {complete && nobodyTicked && (
        <p className="empty">
          Nobody is ticked, so there is nothing to report on. Tick a name above.
        </p>
      )}

      {held.has("reports.students.read") && (
        <div id="report-students" className="report-section-anchor">
          {complete && !nobodyTicked && <Retention {...panels} />}
        </div>
      )}
      {held.has("reports.attendance.read") && (
        <div id="report-attendance" className="report-section-anchor">
          {complete && !nobodyTicked && <AttendanceAnalytics {...panels} />}
        </div>
      )}
      {held.has("reports.payments.read") && (
        <div id="report-payments" className="report-section-anchor">
          {complete && !nobodyTicked && <Payments {...panels} />}
        </div>
      )}
      {held.has("reports.credits.read") && (
        <div id="report-credits" className="report-section-anchor">
          {complete && !nobodyTicked && <Credits {...panels} />}
        </div>
      )}
      {held.has("reports.earnings.read") && (
        <div id="report-earnings" className="report-section-anchor">
          {complete && !nobodyTicked && <Earnings {...panels} />}
        </div>
      )}
      {held.has("reports.utilisation.read") && (
        <div id="report-utilisation" className="report-section-anchor">
          {complete && !nobodyTicked && <Utilisation {...panels} />}
        </div>
      )}
    </>
  );
}

/**
 * What every panel is told: the window, who was asked about, and who they are.
 *
 * `chosen` null is the whole-school request the page has always made; a list is
 * one request per person, drawn as a line each.
 */
interface PanelProps {
  query: ReportQuery;
  chosen: number[] | null;
  names: Map<number, string>;
}

/**
 * A tutor's name, or a generic label when no matching tutor profile is available.
 * Provider ids stay in the report data and requests, not in staff-facing copy.
 */
export function tutorName(names: Map<number, string>, providerId: number): string {
  return names.get(providerId) ?? "Tutor unavailable";
}

/** One figure, with what it is underneath rather than in a legend elsewhere. */
function Stat({
  label,
  value,
  note,
}: {
  label: string;
  value: React.ReactNode;
  note?: string;
}) {
  return (
    <div className="stat">
      <div className="stat-value tabular">{value}</div>
      <div className="small">{label}</div>
      {note && <div className="small muted">{note}</div>}
    </div>
  );
}

function unpaidLessonsPath(query: ReportQuery, providerId?: number): string {
  const params = new URLSearchParams({
    start_date: query.start_date,
    end_date: query.end_date,
    scope: query.scope,
  });
  if (providerId !== undefined) params.set("provider_id", String(providerId));
  return `/reports/unpaid-lessons?${params.toString()}`;
}

/** What the numbers above do and do not cover. Never omitted. */
function Coverage({
  sources,
  extra,
}: {
  sources: ReportingDataSource[];
  extra?: string;
}) {
  return (
    <p className="small muted" style={{ marginBottom: 0 }}>
      {extra && <>{bookingSystemDisplayText(extra)} </>}
      {/* Ended before they are joined. The notes arrive without punctuation, so
          two of them in a row read as one run-on sentence — "…by the WMF
          FastAPI service Snapshot supplied by the Hub…" — under a figure
          somebody is about to quote. */}
      {sources
        .map((source) => {
          const note = source.coverage_note.trim().endsWith(".")
            ? source.coverage_note
            : `${source.coverage_note}.`;
          return bookingSystemDisplayText(note);
        })
        .join(" ")}
    </p>
  );
}

/**
 * The coverage notes off a set of per-person answers.
 *
 * The same three data sources come back on every one of them — they describe
 * where the figures are computed from, not who they are about — so the first
 * answer's notes are the notes. Taken from the answers rather than written out
 * here, because if the far side ever changes what it says, the change should
 * reach the screen without anybody remembering this line exists.
 */
function coverageOf<T extends { data_sources: ReportingDataSource[] }>(
  rows: readonly PersonReport<T>[],
): ReportingDataSource[] {
  return rows[0]?.report.data_sources ?? [];
}

/**
 * The shell every report shares: a heading, the three states, and the body.
 *
 * A 403 here is not an error worth a red box — it means the capability was
 * withdrawn between the page loading and the call, which is a thing that is
 * allowed to happen — so it reads as an absence.
 *
 * A report that cannot be broken down by person is the same kind of thing. The
 * page asked for something the booking system does not answer that way; nothing
 * has gone wrong, and four red boxes in a column would say that something had.
 * So it is drawn as an absence too, with the way to get the figures in it.
 */
function Report<T>({
  title,
  load,
  deps,
  children,
  retryOnError = false,
  hideDataOnError = false,
}: {
  title: string;
  load: () => Promise<T>;
  deps: readonly unknown[];
  children: (data: T) => React.ReactNode;
  retryOnError?: boolean;
  hideDataOnError?: boolean;
}) {
  const { data, error, loading, reload } = useAsync(load, deps);
  return (
    <>
      <h2>{title}</h2>
      <div className="panel">
        {loading && <p className="empty">Loading…</p>}
        {error === NOT_NARROWED && !loading && <p className="empty">{error}</p>}
        {error && error !== NOT_NARROWED && !loading && (
          <div className="error">
            {error}
            {retryOnError && (
              <button
                className="action-tertiary small"
                type="button"
                onClick={reload}
              >
                Try again
              </button>
            )}
          </div>
        )}
        {data && !loading && !(hideDataOnError && error) && children(data)}
      </div>
    </>
  );
}

/**
 * The dependencies a panel reloads on.
 *
 * A fixed length whatever is chosen, because `useAsync` spreads this into a
 * hook's dependency array and React counts them. The people are joined into one
 * string for the same reason.
 */
function reportDeps({ query, chosen }: PanelProps): readonly unknown[] {
  return [
    query.start_date,
    query.end_date,
    query.scope,
    chosen === null ? "" : chosen.join(","),
  ];
}

/**
 * What the two already-per-tutor reports reload on.
 *
 * Not who is ticked. Earnings and the timetable are asked for the whole school
 * and narrowed by dropping rows, so ticking a name is a change to what is
 * drawn and not to what was asked for — refetching there would be a request
 * whose answer is already on the page.
 */
function wholeSchoolDeps({ query }: PanelProps): readonly unknown[] {
  return [query.start_date, query.end_date, query.scope];
}

function Retention(props: PanelProps) {
  const { query, chosen, names } = props;
  const studentScope = query.scope === "mine" ? "mine" : "all";

  if (chosen === null) {
    return (
      <Report
        title="Students"
        load={() => staffBooking.retention(query)}
        deps={reportDeps(props)}
      >
        {(data) => (
          <>
            <div className="stats">
              <Stat
                label="Active registered students"
                value={String(data.registered_students)}
                note="On the books now — people who have left are not counted"
              />
              <Stat
                label="Taught in this period"
                value={String(data.active_students)}
              />
              <Stat label="New" value={String(data.new_students)} />
              <Stat
                label="No future booking recorded"
                value={String(data.students_without_future_booking)}
                note="Review the learner's plans; a booking gap does not mean they have left."
              />
            </div>

            <Attention students={data.attention} scope={studentScope} />
            <OneUpcomingBookingReviews
              reviews={data.one_upcoming_booking_reviews ?? []}
              scope={studentScope}
            />
            <TasterFollowUpReviews
              reviews={data.taster_follow_up_reviews ?? []}
              scope={studentScope}
            />
            <CancellationFollowUpReviews
              reviews={data.cancellation_follow_up_reviews ?? []}
              scope={studentScope}
            />
            <UnusedCreditFollowUpReviews
              reviews={data.unused_credit_follow_up_reviews ?? []}
              scope={studentScope}
            />
            <Coverage sources={data.data_sources} />
          </>
        )}
      </Report>
    );
  }

  return (
    <Report
      title="Students"
      load={() =>
        byProvider(chosen, (id) =>
          staffBooking.retention({ ...query, provider_id: id }),
        )
      }
      deps={reportDeps(props)}
    >
      {(rows) => (
        <>
          <TableScrollRegion
            label="Student retention by tutor"
            minWidth="620px"
          >
            <table>
              <thead>
                <tr>
                  <th>Tutor</th>
                  <th className="tabular">On the books</th>
                  <th className="tabular">Taught</th>
                  <th className="tabular">New</th>
                  <th className="tabular">No future booking</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ providerId, report }) => (
                  <tr key={providerId}>
                    <td>{tutorName(names, providerId)}</td>
                    <td className="tabular">{report.registered_students}</td>
                    <td className="tabular">{report.active_students}</td>
                    <td className="tabular">{report.new_students}</td>
                    <td className="tabular">
                      {report.students_without_future_booking}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScrollRegion>

          <p className="small muted">
            Counts are per tutor. One student taught by multiple selected tutors
            may appear in multiple rows.
          </p>

          <Attention
            students={mergeAttention(rows.map((row) => row.report.attention))}
            scope={studentScope}
          />
          <TasterFollowUpReviews
            reviews={
              rows.length === 1
                ? rows[0]?.report.taster_follow_up_reviews ?? []
                : []
            }
            scope={studentScope}
          />
          <CancellationFollowUpReviews
            reviews={
              rows.length === 1
                ? rows[0]?.report.cancellation_follow_up_reviews ?? []
                : []
            }
            scope={studentScope}
          />
          <UnusedCreditFollowUpReviews
            reviews={
              rows.length === 1
                ? rows[0]?.report.unused_credit_follow_up_reviews ?? []
                : []
            }
            scope={studentScope}
          />
          <Coverage sources={coverageOf(rows)} />
        </>
      )}
    </Report>
  );
}

function CancellationFollowUpReviews({
  reviews,
  scope,
}: {
  reviews: readonly RetentionCancellationFollowUpReview[];
  scope: "mine" | "all";
}) {
  if (reviews.length === 0) return null;

  return (
    <details className="fold">
      <summary>
        {reviews.length === 1
          ? "1 cancelled lesson follow-up to check"
          : `${reviews.length} cancelled lesson follow-ups to check`}
      </summary>
      <p className="small muted">
        These records show a cancellation at least 14 days ago, with no later
        completed private lesson and no future private booking recorded across
        the organisation. They do not show who cancelled or explain the learner&apos;s plans.
      </p>
      <TableScrollRegion label="Cancelled lesson follow-ups to check" minWidth="900px">
        <table>
          <thead>
            <tr>
              <th>Student</th>
              <th>Cancelled lesson</th>
              <th className="tabular">Days since</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {reviews.map((review) => (
              <tr key={review.portal_student_id}>
                <td>
                  <Link to={studentDetailHref(review.portal_student_id, scope)}>
                    {review.display_name}
                  </Link>
                </td>
                <td>{formatDate(review.cancelled_lesson_at)}</td>
                <td className="tabular">{review.days_since_cancellation}</td>
                <td>
                  <div className="row">
                    <Link
                      className="button quiet small"
                      to={studentBookLessonHref(review.portal_student_id, scope)}
                      aria-label={`Open booking options for ${review.display_name}`}
                    >
                      Booking
                    </Link>
                    <RetentionInboxAction
                      studentId={review.portal_student_id}
                      studentName={review.display_name}
                    />
                    <RetentionFollowUpStateAction
                      studentId={review.portal_student_id}
                      studentName={review.display_name}
                      category="cancellation"
                      evidenceAt={review.cancelled_lesson_at}
                    />
                    <RetentionFamilyEmailAction
                      studentId={review.portal_student_id}
                      studentName={review.display_name}
                      category="cancellation"
                      evidenceAt={review.cancelled_lesson_at}
                    />

                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScrollRegion>
    </details>
  );
}

function UnusedCreditFollowUpReviews({
  reviews,
  scope,
}: {
  reviews: readonly RetentionUnusedCreditFollowUpReview[];
  scope: "mine" | "all";
}) {
  if (reviews.length === 0) return null;

  return (
    <details className="fold">
      <summary>
        {reviews.length === 1
          ? "1 unused-credit follow-up to check"
          : `${reviews.length} unused-credit follow-ups to check`}
      </summary>
      <p className="small muted">
        These learners have individually assigned credits that current booking
        rules allow, no future private booking recorded across the organisation,
        and no private lesson, cancellation, or available-credit purchase in
        the last 14 days.
        The dates support a staff review; they do not say why a learner has no
        booking or recommend contacting the learner.
      </p>
      <TableScrollRegion label="Unused-credit follow-ups to check" minWidth="1040px">
        <table>
          <thead>
            <tr>
              <th>Student</th>
              <th>Last private lesson</th>
              <th>Latest available-credit purchase</th>
              <th className="tabular">Days since evidence</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {reviews.map((review) => (
              <tr key={review.portal_student_id}>
                <td>
                  <Link to={studentDetailHref(review.portal_student_id, scope)}>
                    {review.display_name}
                  </Link>
                </td>
                <td>
                  {review.last_completed_private_lesson_at
                    ? formatDate(review.last_completed_private_lesson_at)
                    : "No recorded private lesson"}
                </td>
                <td>{formatDate(review.latest_available_credit_purchased_at)}</td>
                <td className="tabular">{review.days_since_evidence}</td>
                <td>
                  <div className="row">
                    <Link
                      className="button quiet small"
                      to={studentBookLessonHref(review.portal_student_id, scope)}
                      aria-label={`Open booking options for ${review.display_name}`}
                    >
                      Booking
                    </Link>
                    <RetentionInboxAction
                      studentId={review.portal_student_id}
                      studentName={review.display_name}
                    />
                    <RetentionFollowUpStateAction
                      studentId={review.portal_student_id}
                      studentName={review.display_name}
                      category="unused_credit"
                      evidenceAt={laterEvidenceAt(review.last_completed_private_lesson_at, review.latest_available_credit_purchased_at)}
                    />
                    <RetentionFamilyEmailAction
                      studentId={review.portal_student_id}
                      studentName={review.display_name}
                      category="unused_credit"
                      evidenceAt={laterEvidenceAt(review.last_completed_private_lesson_at, review.latest_available_credit_purchased_at)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScrollRegion>
    </details>
  );
}


function Attention({
  students,
  scope,
}: {
  students: readonly RetentionRisk[];
  scope: "mine" | "all";
}) {
  if (students.length === 0)
    return <p className="empty">No active learners have a booking gap in this report.</p>;

  return (
    <details className="fold">
      <summary>
        {students.length === 1
          ? "1 booking gap to review"
          : `${students.length} booking gaps to review`}
      </summary>
      <p className="small muted">
        These dates are a prompt to check current plans. They are not a diagnosis
        that a learner has stopped lessons.
      </p>
      <TableScrollRegion label="Booking gaps to review" minWidth="860px">
        <table>
          <thead>
            <tr>
              <th>Student</th>
              <th>Last lesson</th>
              <th>Next</th>
              <th className="tabular">Days</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {students.map((student) => (
              <tr key={student.portal_student_id}>
                <td>
                  <Link to={studentDetailHref(student.portal_student_id, scope)}>
                    {student.display_name}
                  </Link>{" "}
                  <span className="tag">{student.risk_band}</span>
                </td>
                <td>
                  {student.last_lesson_at
                    ? formatDate(student.last_lesson_at)
                    : "—"}
                </td>
                <td>
                  {student.next_lesson_at
                    ? formatDate(student.next_lesson_at)
                    : "Nothing booked"}
                </td>
                <td className="tabular">
                  {student.days_since_last_lesson ?? "—"}
                </td>
                <td>
                  <div className="row">
                    <Link
                      className="button quiet small"
                      to={studentBookLessonHref(student.portal_student_id, scope)}
                      aria-label={`Open booking options for ${student.display_name}`}
                    >
                      Booking
                    </Link>
                    <RetentionInboxAction
                      studentId={student.portal_student_id}
                      studentName={student.display_name}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScrollRegion>
    </details>
  );
}

function RetentionInboxAction({
  studentId,
  studentName,
}: {
  studentId: string;
  studentName: string;
}) {
  const navigate = useNavigate();
  const { me } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function openConversation() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { threadId } = await inboxApi.openStudentConversation(studentId);
      navigate(`/inbox/${encodeURIComponent(threadId)}`, {
        state: { inboxAccountId: me?.user.id ?? null },
      });
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "We could not open the conversation.",
      );
      setBusy(false);
    }
  }

  return (
    <span>
      <button
        type="button"
        className="quiet small"
        onClick={() => void openConversation()}
        disabled={busy}
        aria-label={`Open Inbox conversation with ${studentName}`}
      >
        {busy ? "Opening…" : "Inbox"}
      </button>
      {error && <span className="small muted" role="alert">{error}</span>}
    </span>
  );
}


function laterEvidenceAt(first: string | null, second: string): string {
  return first && Date.parse(first) > Date.parse(second) ? first : second;
}


function RetentionFamilyEmailAction({
  studentId,
  studentName,
  category,
  evidenceAt,
}: {
  studentId: string;
  studentName: string;
  category: RetentionFollowUpCategory;
  evidenceAt: string;
}) {
  const [status, setStatus] = useState<RetentionFamilyFollowUpEmailStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    void retentionFollowUpApi
      .emailStatus({ studentId, category, evidenceAt })
      .then((result) => {
        if (active) setStatus(result);
      })
      .catch((cause) => {
        if (active) setError(cause instanceof Error ? cause.message : "Could not check family email preferences.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [studentId, category, evidenceAt]);

  async function queueEmail() {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await retentionFollowUpApi.queueFamilyEmail({ studentId, category, evidenceAt });
      setQueued(true);
      setStatus(await retentionFollowUpApi.emailStatus({ studentId, category, evidenceAt }));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not queue the family email.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return null;
  if (error && !status?.enabled) {
    return <span className="small muted" role="alert">{error}</span>;
  }
  if (!status?.enabled) return null;
  if (status.state === "ready") {
    return (
      <span className="retention-follow-up-email">
        <button
          type="button"
          className="quiet small"
          onClick={() => void queueEmail()}
          disabled={saving}
          aria-label={`Queue optional family follow-up email for ${studentName}`}
        >
          {saving ? "Queueing…" : "Queue family email"}
        </button>
        {error && <span className="small muted" role="alert">{error}</span>}
      </span>
    );
  }
  if (error) return <span className="small muted" role="alert">{error}</span>;
  if (queued || status.state === "already_queued") {
    return <span className="small muted" role="status">Optional email queued; 30-day limit applies.</span>;
  }
  if (status.state === "cooldown" && status.availableAt) {
    return <span className="small muted" role="status">Next optional email after {formatDate(status.availableAt)}.</span>;
  }
  if (status.state === "no_opt_in") {
    return <span className="small muted">No confirmed guardian has opted in.</span>;
  }
  return null;
}

function RetentionFollowUpStateAction({
  studentId,
  studentName,
  category,
  evidenceAt,
}: {
  studentId: string;
  studentName: string;
  category: RetentionFollowUpCategory;
  evidenceAt: string;
}) {
  const [state, setState] = useState<RetentionFollowUpStatus>("open");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    void retentionFollowUpApi
      .state({ studentId, category, evidenceAt })
      .then((result) => {
        if (!active) return;
        setState(result.state);
        setNote(result.note ?? "");
      })
      .catch((cause) => {
        if (!active) return;
        setError(cause instanceof Error ? cause.message : "Could not load follow-up state.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [studentId, category, evidenceAt]);

  async function save(action: "dismiss" | "resolve" | "reopen") {
    if (saving || loading) return;
    setSaving(true);
    setError(null);
    try {
      const result = await retentionFollowUpApi.update({
        studentId,
        category,
        evidenceAt,
        action,
        note: note.trim() || null,
      });
      setState(result.state);
      setNote(result.note ?? "");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save follow-up state.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <span className="retention-follow-up-state">
      {loading ? (
        <span className="small muted" aria-live="polite">Loading status…</span>
      ) : (
        <>
          <span className="small" aria-live="polite">
            {state === "open" ? "Needs review" : state === "dismissed" ? "Dismissed" : "Resolved"}
          </span>
          {state === "open" ? (
            <>
              <input
                value={note}
                onChange={(event) => setNote(event.target.value)}
                maxLength={500}
                aria-label={`Optional staff note for ${studentName}`}
                placeholder="Optional staff note"
                disabled={saving}
              />
              <button
                type="button"
                className="quiet small"
                onClick={() => void save("dismiss")}
                disabled={saving}
                aria-label={`Dismiss follow-up for ${studentName}`}
              >
                {saving ? "Saving…" : "Dismiss"}
              </button>
              <button
                type="button"
                className="quiet small"
                onClick={() => void save("resolve")}
                disabled={saving}
                aria-label={`Resolve follow-up for ${studentName}`}
              >
                Resolve
              </button>
            </>
          ) : (
            <button
              type="button"
              className="quiet small"
              onClick={() => void save("reopen")}
              disabled={saving}
              aria-label={`Reopen follow-up for ${studentName}`}
            >
              {saving ? "Saving…" : "Reopen"}
            </button>
          )}
        </>
      )}
      {error && <span className="small muted" role="alert">{error}</span>}
    </span>
  );
}

function OneUpcomingBookingReviews({
  reviews,
  scope,
}: {
  reviews: readonly RetentionBookingPatternReview[];
  scope: "mine" | "all";
}) {
  if (reviews.length === 0) return null;

  return (
    <details className="fold">
      <summary>
        {reviews.length === 1
          ? "1 upcoming booking to check"
          : `${reviews.length} learners with one upcoming booking to check`}
      </summary>
      <p className="small muted">
        The report records at least two past lessons and exactly one future
        booking in this report&apos;s teaching scope. Check whether more lessons
        are planned; this is a review prompt, not a diagnosis.
      </p>
      <TableScrollRegion label="Learners with one upcoming booking" minWidth="900px">
        <table>
          <thead>
            <tr>
              <th>Student</th>
              <th>Last lesson</th>
              <th>Next lesson</th>
              <th className="tabular">Past lessons</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {reviews.map((review) => (
              <tr key={review.portal_student_id}>
                <td>
                  <Link to={studentDetailHref(review.portal_student_id, scope)}>
                    {review.display_name}
                  </Link>
                </td>
                <td>{formatDate(review.last_lesson_at)}</td>
                <td>{formatDate(review.next_lesson_at)}</td>
                <td className="tabular">{review.completed_lesson_count}</td>
                <td>
                  <div className="row">
                    <Link
                      className="button quiet small"
                      to={studentBookLessonHref(review.portal_student_id, scope)}
                      aria-label={`Open booking options for ${review.display_name}`}
                    >
                      Booking
                    </Link>
                    <RetentionInboxAction
                      studentId={review.portal_student_id}
                      studentName={review.display_name}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScrollRegion>
    </details>
  );
}

export function paymentProviderPageProblem(
  report: PaymentReport,
  request: PaymentReportQuery,
): string | null {
  const page = report.provider_page;
  if (!page) return null;

  const expectedTotalPages = Math.max(1, Math.ceil(page.total / page.limit));
  const expectedPage = Math.min(request.page ?? 1, expectedTotalPages);
  if (
    !Number.isInteger(page.total) ||
    page.total < 0 ||
    page.limit !== (request.limit ?? DENSE_ROW_PAGE_SIZE) ||
    !Number.isInteger(page.limit) ||
    page.limit < 1 ||
    !Number.isInteger(page.total_pages) ||
    page.total_pages !== expectedTotalPages ||
    !Number.isInteger(page.page) ||
    page.page !== expectedPage ||
    page.items.length > page.limit
  ) {
    return "The payment report returned incomplete page details. Try again.";
  }

  // The service names the scope it applied, not the one asked for: a "mine"
  // request is answered as the signed provider's own ("provider") report.
  if (
    report.period.start_date !== request.start_date ||
    report.period.end_date !== request.end_date ||
    report.period.scope !== (request.scope === "mine" ? "provider" : "organisation")
  ) {
    return "The payment report did not match the selected dates or scope. Try again.";
  }

  const requestedIds = request.provider_ids
    ? [...new Set(request.provider_ids)].sort((a, b) => a - b)
    : request.scope === "mine"
      ? report.period.provider_id === null
        ? []
        : [report.period.provider_id]
      : null;
  const returnedIds = page.provider_ids;
  if (
    requestedIds === null
      ? returnedIds !== null
      : returnedIds === null ||
        page.total !== requestedIds.length ||
        returnedIds.length !== requestedIds.length ||
        returnedIds.some((id, index) => id !== requestedIds[index])
  ) {
    return "The payment report did not confirm the selected tutor scope. No figures are shown.";
  }

  if (request.scope === "mine" && report.period.provider_id === null) {
    return "The payment report did not confirm the signed tutor scope. No figures are shown.";
  }

  const itemIds = page.items.map((item) => item.provider_id);
  if (requestedIds !== null) {
    const expectedItemIds = requestedIds.slice(
      (page.page - 1) * page.limit,
      page.page * page.limit,
    );
    if (
      itemIds.length !== expectedItemIds.length ||
      itemIds.some((id, index) => id !== expectedItemIds[index])
    ) {
      return "The payment report returned tutor rows outside the selected scope. No figures are shown.";
    }
  }

  if (
    itemIds.some((id) => !Number.isInteger(id) || id < 1) ||
    new Set(itemIds).size !== itemIds.length ||
    itemIds.some((id, index) => index > 0 && id < (itemIds[index - 1] ?? 0))
  ) {
    return "The payment report returned duplicate or unordered tutor rows. Try again.";
  }

  return null;
}

async function loadPaymentReport(request: PaymentReportQuery): Promise<PaymentReport> {
  const report = await staffBooking.payments(request);
  const problem = paymentProviderPageProblem(report, request);
  if (problem) throw new Error(problem);
  return report;
}

function paymentReportIdentity({ query, chosen }: PanelProps): string {
  return JSON.stringify([
    query.start_date,
    query.end_date,
    query.scope,
    chosen === null ? null : [...chosen].sort((a, b) => a - b),
  ]);
}

function PaymentTotals({ data, query }: { data: PaymentReport; query: ReportQuery }) {
  return (
    <>
      <div className="stats">
        <Stat label="Taken" value={`£${data.confirmed_gross}`} />
        <Stat label="Refunded" value={`£${data.refunds}`} />
        <Stat label="Net" value={`£${data.confirmed_net}`} />
        <Stat
          label="Lessons past and unpaid"
          value={
            <Link to={unpaidLessonsPath(query)}>
              {data.past_unpaid_lessons}
            </Link>
          }
          note="Chase these"
        />
        <Stat
          label="Waiting for a tutor"
          value={String(data.pending_on_site_requests)}
        />
      </div>
      {data.by_method.length > 0 && (
        <TableScrollRegion label="Payment methods" minWidth="520px">
          <table>
            <thead>
              <tr>
                <th>How</th>
                <th className="tabular">Payments</th>
                <th className="tabular">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.by_method.map((row) => (
                <tr key={row.payment_method}>
                  <td>{paymentMethodLabel(row.payment_method)}</td>
                  <td className="tabular">{row.transaction_count}</td>
                  <td className="tabular">£{row.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScrollRegion>
      )}
    </>
  );
}

function PaymentProviderTable({
  page,
  names,
  query,
}: {
  page: PaymentProviderPage;
  names: Map<number, string>;
  query: ReportQuery;
}) {
  if (page.items.length === 0) {
    return <p className="empty">There are no reportable tutors in this scope.</p>;
  }

  return (
    <>
      <p className="small muted">
        Showing {page.items.length} of {page.total} tutors. The totals above cover
        the complete selected scope.
      </p>
      <TableScrollRegion label="Payments by tutor" minWidth="720px">
        <table>
          <thead>
            <tr>
              <th>Tutor</th>
              <th className="tabular">Taken</th>
              <th className="tabular">Refunded</th>
              <th className="tabular">Net</th>
              <th className="tabular">Past and unpaid</th>
              <th className="tabular">Waiting</th>
            </tr>
          </thead>
          <tbody>
            {page.items.map((row) => (
              <tr key={row.provider_id}>
                <td>{tutorName(names, row.provider_id)}</td>
                <td className="tabular">£{row.confirmed_gross}</td>
                <td className="tabular">£{row.refunds}</td>
                <td className="tabular">£{row.confirmed_net}</td>
                <td className="tabular">
                  <Link to={unpaidLessonsPath(query, row.provider_id)}>
                    {row.past_unpaid_lessons}
                  </Link>
                </td>
                <td className="tabular">{row.pending_on_site_requests}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScrollRegion>
    </>
  );
}

function Payments(props: PanelProps) {
  const { query, chosen, names } = props;
  const identity = paymentReportIdentity(props);
  const [pagination, setPagination] = useState({ identity, page: 1 });
  const page = pagination.identity === identity ? pagination.page : 1;
  useEffect(() => {
    setPagination((current) =>
      current.identity === identity ? current : { identity, page: 1 },
    );
  }, [identity]);

  const request: PaymentReportQuery = {
    ...query,
    page,
    limit: DENSE_ROW_PAGE_SIZE,
    ...(chosen === null
      ? {}
      : { provider_ids: [...chosen].sort((a, b) => a - b) }),
  };

  return (
    <Report
      key={identity}
      title="Payments"
      load={() => loadPaymentReport(request)}
      deps={[...reportDeps(props), page]}
      retryOnError
      hideDataOnError
    >
      {(data) => {
        const providerPage = data.provider_page;
        if (!providerPage && chosen !== null) {
          return (
            <p className="notice" role="status">
              No figures are shown because the reporting service did not confirm
              the selected tutor filter. Its paged provider-filter contract must
              be available first.
            </p>
          );
        }

        return (
          <>
            <PaymentTotals data={data} query={query} />
            {providerPage ? (
              <>
                <PaymentProviderTable
                  page={providerPage}
                  names={names}
                  query={query}
                />
                <Pagination
                  page={providerPage.page}
                  pages={providerPage.total_pages}
                  ariaLabel="Payments by tutor pages"
                  onPageChange={(nextPage) =>
                    setPagination({ identity, page: nextPage })
                  }
                />
              </>
            ) : (
              <p className="notice" role="status">
                Period totals are complete, but tutor-by-tutor pages are not
                available from the reporting service yet.
              </p>
            )}
            <Coverage sources={data.data_sources} />
          </>
        );
      }}
    </Report>
  );
}

function Credits(props: PanelProps) {
  const { query, chosen, names } = props;

  if (chosen === null) {
    return (
      <Report
        title="Lesson blocks"
        load={() => staffBooking.credits(query)}
        deps={reportDeps(props)}
      >
        {(data) => (
          <>
            <div className="stats">
              <Stat
                label="Blocks sold in this period"
                value={String(data.blocks_sold)}
              />
              <Stat
                label="Credits issued without a sale"
                value={String(data.credits_issued_without_sale ?? 0)}
                note="Goodwill, carried-forward, transferred and paid-cancellation credits"
              />
              <Stat
                label="Blocks still open"
                value={String(data.active_blocks)}
              />
              <Stat
                label="Credits outstanding"
                value={String(data.outstanding_credits)}
              />
              <Stat
                label="Expiring within 30 days"
                value={String(data.expiring_within_30_days)}
                note="Lessons families have paid for and not booked"
              />
              <Stat
                label="Owed in lessons"
                value={`£${data.outstanding_liability}`}
                note="An estimate, not an accounting balance"
              />
            </div>
            <p className="small muted">
              {data.transactions.granted} granted · {data.transactions.consumed}{" "}
              used · {data.transactions.restored} put back
            </p>
            <Coverage sources={data.data_sources} />
          </>
        )}
      </Report>
    );
  }

  return (
    <Report
      title="Lesson blocks"
      load={() =>
        byProvider(chosen, (id) =>
          staffBooking.credits({ ...query, provider_id: id }),
        )
      }
      deps={reportDeps(props)}
    >
      {(rows) => (
        <>
          <TableScrollRegion label="Lesson credits by tutor" minWidth="700px">
            <table>
              <thead>
                <tr>
                  <th>Tutor</th>
                  <th className="tabular">Sold</th>
                  <th className="tabular">Still open</th>
                  <th className="tabular">Credits outstanding</th>
                  <th className="tabular">Expiring in 30 days</th>
                  <th className="tabular">Owed in lessons</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ providerId, report }) => (
                  <tr key={providerId}>
                    <td>{tutorName(names, providerId)}</td>
                    <td className="tabular">{report.blocks_sold}</td>
                    <td className="tabular">{report.active_blocks}</td>
                    <td className="tabular">{report.outstanding_credits}</td>
                    <td className="tabular">
                      {report.expiring_within_30_days}
                    </td>
                    <td className="tabular">£{report.outstanding_liability}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScrollRegion>
          {/* Not totalled, and for a different reason than the students table:
              a block is bought by a family, not from a tutor, and the credits
              in it can be spent on whoever is free that week. Per tutor these
              are "blocks whose lessons were taught by them", which is a useful
              thing to compare and not a thing to add up. */}
          <p className="small muted">
            A lesson block belongs to a family rather than to a tutor, and its
            credits can be spent with any of them, so these lines are for
            comparing and not for adding up.
          </p>
          <Coverage sources={coverageOf(rows)} />
        </>
      )}
    </Report>
  );
}

/**
 * Whether a provider row is one of the people asked about.
 *
 * Earnings and the timetable arrive keyed by provider already, so narrowing
 * them is dropping rows rather than asking again — one request either way, and
 * exactly the same figures as the whole-school view shows on those lines.
 */
function wanted(chosen: number[] | null, providerId: number): boolean {
  return chosen === null || chosen.includes(providerId);
}

/** What an empty table says, which depends on whether anybody was picked. */
function nothingFor(chosen: number[] | null, whole: string): string {
  return chosen === null
    ? whole
    : "Nothing for the staff you have ticked in this period.";
}

function Earnings(props: PanelProps) {
  const { query, chosen, names } = props;

  return (
    <Report
      title="Earnings by tutor"
      load={() => staffBooking.earnings(query)}
      deps={wholeSchoolDeps(props)}
    >
      {(data) => {
        const providers = data.providers.filter((provider) =>
          wanted(chosen, provider.provider_id),
        );

        return (
          <>
            {providers.length === 0 && (
              <p className="empty">
                {nothingFor(chosen, "Nothing confirmed in this period.")}
              </p>
            )}
            {providers.length > 0 && (
              <TableScrollRegion label="Tutor earnings" minWidth="620px">
                <table>
                  <thead>
                    <tr>
                      <th>Tutor</th>
                      <th className="tabular">Payments</th>
                      <th className="tabular">Gross</th>
                      <th className="tabular">Refunds</th>
                      <th className="tabular">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {providers.map((provider) => (
                      <tr key={provider.provider_id}>
                        <td>{tutorName(names, provider.provider_id)}</td>
                        <td className="tabular">
                          {provider.confirmed_transactions}
                        </td>
                        <td className="tabular">£{provider.confirmed_gross}</td>
                        <td className="tabular">£{provider.refunds}</td>
                        <td className="tabular">£{provider.confirmed_net}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScrollRegion>
            )}
            <Coverage sources={data.data_sources} extra={data.coverage + "."} />
          </>
        );
      }}
    </Report>
  );
}

function Utilisation(props: PanelProps) {
  const { query, chosen, names } = props;

  return (
    <Report
      title="Timetable"
      load={() => staffBooking.utilisation(query)}
      deps={wholeSchoolDeps(props)}
    >
      {(data) => {
        const providers = data.providers.filter((provider) =>
          wanted(chosen, provider.provider_id),
        );

        return (
          <>
            {providers.length === 0 && (
              <p className="empty">
                {nothingFor(chosen, "No lessons in this period.")}
              </p>
            )}
            {providers.length > 0 && (
              <TableScrollRegion
                label="Tutor timetable utilisation"
                minWidth="820px"
              >
                <table>
                  <thead>
                    <tr>
                      <th>Tutor</th>
                      <th className="tabular">Lessons</th>
                      <th className="tabular">Hours taught</th>
                      <th className="tabular">Days in</th>
                      <th className="tabular">Gaps</th>
                      <th className="tabular">Hours available</th>
                      <th className="tabular">Used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {providers.map((provider) => (
                      <tr key={provider.provider_id}>
                        <td>{tutorName(names, provider.provider_id)}</td>
                        <td className="tabular">
                          {provider.appointment_count}
                        </td>
                        <td className="tabular">
                          {(provider.booked_minutes / 60).toFixed(1)}
                        </td>
                        <td className="tabular">{provider.teaching_days}</td>
                        <td className="tabular">
                          {(provider.timetable_gap_minutes / 60).toFixed(1)}h
                        </td>
                        <td className="tabular">
                          {provider.available_minutes === null
                            ? "—"
                            : (provider.available_minutes / 60).toFixed(1)}
                        </td>
                        {/* A dash rather than 0%. A tutor with no working hours set
                          in the booking system has an unknown denominator, and printing a
                          percentage for that would read as "does nothing". */}
                        <td className="tabular">
                          {provider.utilisation_rate_percent === null
                            ? "—"
                            : `${provider.utilisation_rate_percent}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScrollRegion>
            )}
            <Coverage
              sources={data.data_sources}
              extra="Hours available come from each tutor's schedule in the booking system as it stands today, so a rota that has since changed will not match a period in the past. A dash means no working hours are set in the booking system for that tutor."
            />
          </>
        );
      }}
    </Report>
  );
}


function TasterFollowUpReviews({
  reviews,
  scope,
}: {
  reviews: readonly RetentionTasterFollowUpReview[];
  scope: "mine" | "all";
}) {
  if (reviews.length === 0) return null;

  return (
    <details className="fold">
      <summary>
        {reviews.length === 1
          ? "1 taster follow-up to check"
          : `${reviews.length} taster follow-ups to check`}
      </summary>
      <p className="small muted">
        These records show a configured taster lesson at least 14 days ago, with
        no later regular lesson or regular booking recorded across the organisation.
        Check the learner&apos;s plans; the record does not explain their intentions.
      </p>
      <TableScrollRegion label="Taster follow-ups to check" minWidth="900px">
        <table>
          <thead>
            <tr>
              <th>Student</th>
              <th>Last taster lesson</th>
              <th className="tabular">Days since</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {reviews.map((review) => (
              <tr key={review.portal_student_id}>
                <td>
                  <Link to={studentDetailHref(review.portal_student_id, scope)}>
                    {review.display_name}
                  </Link>
                </td>
                <td>{formatDate(review.last_taster_lesson_at)}</td>
                <td className="tabular">{review.days_since_taster}</td>
                <td>
                  <div className="row">
                    <Link
                      className="button quiet small"
                      to={studentBookLessonHref(review.portal_student_id, scope)}
                      aria-label={`Open booking options for ${review.display_name}`}
                    >
                      Booking
                    </Link>
                    <RetentionInboxAction
                      studentId={review.portal_student_id}
                      studentName={review.display_name}
                    />
                    <RetentionFollowUpStateAction
                      studentId={review.portal_student_id}
                      studentName={review.display_name}
                      category="taster"
                      evidenceAt={review.last_taster_lesson_at}
                    />
                    <RetentionFamilyEmailAction
                      studentId={review.portal_student_id}
                      studentName={review.display_name}
                      category="taster"
                      evidenceAt={review.last_taster_lesson_at}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScrollRegion>
    </details>
  );
}
